// View model for the eligible banner: the comp ready to issue, the numbers it is based on, and
// the wagering (from tags, or set manually when tags do not decide it). Amounts stay decimal
// strings; multiplying keeps the currency's decimals.
import { formatAmount, formatDateTimeUTC, storedAmount, trimDecimal } from './format';
import { formatShortUTC } from './timeline';
import { formatMoney, TOTAL_CHECKS } from './notEligible';

const WAGERING_NOT_DETERMINED = 'Wagering not determined';
const MAX_AMOUNT_DECIMALS = 8;

const EYEBROWS = {
  eligible: { eyebrow: `Eligible · All ${TOTAL_CHECKS} checks passed`, tone: 'positive' },
  needs_exception: { eyebrow: `Eligible with exception · All ${TOTAL_CHECKS} checks ran`, tone: 'positive' },
  manual_check: { eyebrow: `Manual check · All ${TOTAL_CHECKS} checks ran`, tone: 'warning' },
};

// isValidAmount: a positive decimal with at most 8 decimals (what the back office accepts).
export const isValidAmount = (s) =>
  new RegExp(`^\\d+(\\.\\d{1,${MAX_AMOUNT_DECIMALS}})?$`).test(String(s)) && Number(s) > 0;

// multiplyDecimal multiplies a decimal string by a whole factor without float rounding; the
// result keeps the input's number of decimals.
export const multiplyDecimal = (amount, factor) => {
  const [int, frac = ''] = String(amount).split('.');
  const digits = String(Number(`${int}${frac}`) * factor).padStart(frac.length + 1, '0');
  return frac.length ? `${digits.slice(0, -frac.length)}.${digits.slice(-frac.length)}` : digits;
};

function amountView(p, deposits, adjusted) {
  const currency = p.issue_amount ? p.issue_currency : p.basis_currency;
  const proposed = p.issue_amount
    ? formatAmount(storedAmount(p.issue_amount), p.issue_currency)
    : `convert ${formatMoney(p.basis_amount, p.basis_currency)} to ${p.issue_currency}`;
  if (adjusted) {
    return { value: formatAmount(adjusted, currency), sub: `adjusted from ${proposed}`, adjusted: true, currency };
  }
  const basis = `${p.rate_percent}% × ${formatMoney(deposits.measured, deposits.basis_currency)} → ${formatMoney(p.basis_amount, p.basis_currency)}`;
  const rate = p.issue_rate_usd ? ` · at today's ${formatAmount(trimDecimal(p.issue_rate_usd))} USD/${p.issue_currency}` : '';
  return { value: proposed, sub: `= ${basis}, rounded to ${p.round_to}, min ${p.min_amount}${rate}`, adjusted: false, currency };
}

function wageringView(check, manual, amount, currency) {
  const fromTags = check.proposal.wagering;
  const missing = fromTags === null || fromTags === undefined;
  const value = missing ? (manual ?? null) : fromTags;
  const toWager = value !== null && amount ? formatAmount(multiplyDecimal(amount, value), currency) : null;
  return { missing, value, toWager, matches: check.wagering?.matches || [] };
}

// eligibleView describes a comp that can be issued. overrides: { wagering, amount } entered by
// the manager (wagering only applies when tags did not decide it).
export const eligibleView = (check, overrides = {}) => {
  const p = check.proposal;
  const { deposits, balance } = check;
  const period = check.period?.period || {};
  const meta = EYEBROWS[check.decision] || EYEBROWS.eligible;
  const amount = amountView(p, deposits, overrides.amount);
  const count = (deposits.by_currency || []).reduce((n, d) => n + (d.count || 0), 0);
  // No to-wager figure while the amount still needs converting to the issue currency.
  const wagering = wageringView(check, overrides.wagering, overrides.amount || p.issue_amount || null, amount.currency);
  return {
    ...meta,
    title: `${p.name} ready to issue`,
    text: `${p.rate_percent}% of deposits in the active period ${formatShortUTC(period.period_start)} → ${formatShortUTC(period.period_end)} UTC.`
      + ` Valid ${p.valid_days} days, activatable until ${formatDateTimeUTC(p.activatable_until)}.`,
    metrics: [
      { label: 'Deposited this period', value: formatMoney(deposits.measured, deposits.basis_currency), sub: `${count} deposit${count === 1 ? '' : 's'}` },
      { label: 'Rate', value: `× ${p.rate_percent}%`, sub: 'of deposits, VIP comp rule' },
      { label: 'Balance', value: formatMoney((balance.total_usd_cents / 100).toFixed(2), 'USD'), sub: `below ${formatMoney((balance.limit_usd_cents / 100).toFixed(2), 'USD')}` },
    ],
    amount: { value: amount.value, sub: amount.sub, adjusted: amount.adjusted },
    wagering,
    reasons: (check.reasons || []).filter((r) => !(wagering.missing && r.startsWith(WAGERING_NOT_DETERMINED))),
  };
};
